/**
 * Documents Centre — pure client model helpers.
 *
 * Everything the centre list + preview modal need that is not React:
 * the scan-engine badge (ApplicantDetail's #1223 idiom), the AI
 * verdict chip, admin-safe error copy (D4), the id-based multi-
 * selection model (R1, cap 100), the one-job selection payload +
 * outcome summary (R2), the server pagination query + tab/count
 * mapping (R3), and the background-review job status vocabulary.
 *
 * Pure and dependency-free so the canonical jest suite (tests/) can
 * pin every behaviour without a DOM.
 */

import type { BulkReviewJobItemOutcome } from './bulkReviewJobSummary';

// ── Server item stamps (PR-A projection) ───────────────────────────

export interface DocumentExtractionStamp {
  engine: string | null;
  engineNote: string | null;
  status: string | null;
  processedAt: string | null;
  overallConfidence: number | null;
}

export interface DocumentVerdictSummary {
  suggestedStatus: string | null;
  identityStatus: string | null;
  /** The review's reason (the "why"), from the judgment stamp. */
  reason: string | null;
  checkedAt: string | null;
}

// ── Scan engine badge (ApplicantDetail idiom, PR #1223) ────────────

/**
 * The engine label vocabulary — copied verbatim from ApplicantDetail's
 * scanEngineLabel so a document reads identically on both surfaces.
 * Returns null for legacy records without an engine stamp.
 */
export function scanEngineLabel(engine: string | null | undefined): string | null {
  switch (engine) {
    case 'document-ai': return 'Scanned by Document AI';
    case 'document-ai-id': return 'Scanned by Document AI (ID proofing)';
    case 'tesseract-ocr': return 'Scanned by built-in OCR';
    case 'pdf-text': return 'Text read by built-in PDF parser';
    case 'docx-text': return 'Text read by built-in Word parser';
    case 'unreadable': return 'Could not be read automatically';
    default: return null;
  }
}

export interface DocumentEngineDescription {
  label: string;
  /** Tooltip — the honest fallback reason when one was recorded. */
  title: string;
  scanned: boolean;
}

/**
 * Describe a document's scan state for the badge. The PR-A projection
 * nulls every extraction field when the document was never scanned,
 * so an all-null stamp truthfully renders "Not scanned" (where
 * ApplicantDetail, reading legacy records, renders nothing).
 */
export function describeDocumentEngine(
  extraction: DocumentExtractionStamp | null | undefined
): DocumentEngineDescription {
  const scanned = !!(
    extraction &&
    (extraction.engine || extraction.processedAt || extraction.status)
  );
  if (!scanned) {
    return {
      label: 'Not scanned',
      title: 'This document has not been scanned yet.',
      scanned: false,
    };
  }
  const label =
    scanEngineLabel(extraction?.engine) ||
    (extraction?.engine ? `Scanned by ${extraction.engine}` : 'Scanned');
  return {
    label,
    title: extraction?.engineNote || 'Engine that read this document',
    scanned: true,
  };
}

// ── AI verdict chip ────────────────────────────────────────────────

export type VerdictTone = 'emerald' | 'red' | 'amber' | 'slate';

export interface DocumentVerdictDescription {
  label: string;
  tone: VerdictTone;
}

/**
 * The AI verdict as a first-class chip, distinct from the human
 * `status`: it renders the consolidated review's suggested status from
 * the PR-A `verdict` projection. No verdict recorded → no chip.
 */
export function describeDocumentVerdict(
  verdict: DocumentVerdictSummary | null | undefined
): DocumentVerdictDescription | null {
  const suggested = verdict?.suggestedStatus;
  if (!suggested) return null;
  switch (suggested) {
    case 'Approved':
      return { label: 'AI verdict: Approved', tone: 'emerald' };
    case 'SystemPreApproved':
      return { label: 'AI verdict: Pre-approved', tone: 'emerald' };
    case 'Rejected':
      return { label: 'AI verdict: Rejected', tone: 'red' };
    case 'PendingAdminReview':
      return { label: 'AI verdict: Needs human review', tone: 'amber' };
    default:
      return { label: `AI verdict: ${suggested}`, tone: 'slate' };
  }
}

/** Identity cross-check mismatch — the ApplicantDetail warning idiom. */
export function hasIdentityMismatch(args: {
  verdict?: DocumentVerdictSummary | null;
  judgment?: any;
  aiReview?: any;
}): boolean {
  return (
    args.verdict?.identityStatus === 'mismatch' ||
    args.judgment?.identityStatus === 'mismatch' ||
    args.aiReview?.identityStatus === 'mismatch'
  );
}

/**
 * A document a human (or the auto-apply rule) has not finally decided:
 * the centre may offer it a system review and a human decision.
 * Superseded versions are never review candidates.
 */
export function isDocumentUndecided(doc: {
  status?: string | null;
  superseded?: boolean | null;
}): boolean {
  if (doc.superseded) return false;
  return doc.status !== 'Approved' && doc.status !== 'Rejected';
}

// ── Admin-safe error copy (D4) ─────────────────────────────────────

export interface AdminSafeError {
  /** Admin-facing copy — the ONLY text rendered as page content. */
  safe: string;
  /** The raw server/transport message, for console + a collapsed
   *  "Technical details" affordance. Never page content. */
  raw: string;
}

/**
 * Map any fetch failure to admin-safe copy. Raw server strings must
 * never render as page content (2026-10-17: a raw ReferenceError from
 * the server was presented to the owner as the Documents page's error
 * text). The raw message is preserved on `raw` for support.
 */
export function adminSafeError(err: any, fallback: string): AdminSafeError {
  const raw = String(err?.message || err || 'Unknown error');
  const status = typeof err?.status === 'number' ? err.status : null;

  let safe: string;
  if (status === 400) {
    safe = 'That request was not accepted. Refresh the list and try again.';
  } else if (status === 401) {
    safe = 'Your session has expired. Please sign in again.';
  } else if (status === 403) {
    safe = 'You do not have permission to do this in this workspace.';
  } else if (status === 404) {
    safe = 'That record could not be found. It may have been removed — refresh the list.';
  } else if (status === 409) {
    safe = 'This changed while you were working. Refresh the list and try again.';
  } else if (status !== null && status >= 500) {
    safe = 'Something went wrong on the server. Please try again — if it keeps happening, contact support.';
  } else if (status !== null) {
    safe = fallback;
  } else if (/network|fetch|load failed|connection/i.test(raw)) {
    safe = 'Could not reach the server. Check your connection and try again.';
  } else {
    safe = fallback;
  }
  return { safe, raw };
}

// ── Selection model (R1) ───────────────────────────────────────────

export const DOCUMENT_SELECTION_CAP = 100;

export interface DocumentSelectionEntry {
  applicationId: string;
  docId: string;
  documentName: string;
}

/**
 * Id-based selection: a document stays selected across page changes,
 * tab switches and view (flat/grouped) switches because the selection
 * carries everything a submission needs, not just an id into the
 * currently rendered page.
 */
export type DocumentSelection = Map<string, DocumentSelectionEntry>;

export function selectionEntryOf(doc: {
  applicationId: string;
  docId: string;
  documentName: string;
}): DocumentSelectionEntry {
  return {
    applicationId: doc.applicationId,
    docId: doc.docId,
    documentName: doc.documentName,
  };
}

export function toggleDocumentSelection(
  selection: DocumentSelection,
  doc: { applicationId: string; docId: string; documentName: string },
  cap: number = DOCUMENT_SELECTION_CAP
): { next: DocumentSelection; capped: boolean } {
  const next = new Map(selection);
  if (next.has(doc.docId)) {
    next.delete(doc.docId);
    return { next, capped: false };
  }
  if (next.size >= cap) {
    return { next: selection, capped: true };
  }
  next.set(doc.docId, selectionEntryOf(doc));
  return { next, capped: false };
}

export function setGroupDocumentSelection(
  selection: DocumentSelection,
  docs: Array<{ applicationId: string; docId: string; documentName: string }>,
  select: boolean,
  cap: number = DOCUMENT_SELECTION_CAP
): { next: DocumentSelection; cappedCount: number } {
  const next = new Map(selection);
  let cappedCount = 0;
  if (!select) {
    for (const doc of docs) next.delete(doc.docId);
    return { next, cappedCount };
  }
  for (const doc of docs) {
    if (next.has(doc.docId)) continue;
    if (next.size >= cap) {
      cappedCount += 1;
      continue;
    }
    next.set(doc.docId, selectionEntryOf(doc));
  }
  return { next, cappedCount };
}

export function groupSelectionState(
  selection: DocumentSelection,
  docs: Array<{ docId: string }>
): 'none' | 'some' | 'all' {
  if (docs.length === 0) return 'none';
  const selected = docs.filter(d => selection.has(d.docId)).length;
  if (selected === 0) return 'none';
  if (selected === docs.length) return 'all';
  return 'some';
}

// ── One-job submit (R2) ────────────────────────────────────────────

/** The selection-mode payload: ONE job for the whole selection. */
export function buildSelectionJobPayload(selection: DocumentSelection): {
  documents: Array<{ applicationId: string; docId: string }>;
} {
  return {
    documents: [...selection.values()].map(e => ({
      applicationId: e.applicationId,
      docId: e.docId,
    })),
  };
}

export interface SelectionOutcome {
  applicationId: string;
  docId: string;
  outcome: 'accepted' | 'skipped' | 'alreadyRunning';
  reason?: string;
  jobId?: string;
}

export interface SelectionOutcomeSummary {
  accepted: SelectionOutcome[];
  alreadyRunning: SelectionOutcome[];
  skipped: SelectionOutcome[];
  /** One-line honest summary for the toast / submit panel. */
  headline: string;
}

export function summarizeSelectionOutcomes(
  outcomes: SelectionOutcome[] | null | undefined
): SelectionOutcomeSummary {
  const list = Array.isArray(outcomes) ? outcomes : [];
  const accepted = list.filter(o => o.outcome === 'accepted');
  const alreadyRunning = list.filter(o => o.outcome === 'alreadyRunning');
  const skipped = list.filter(o => o.outcome === 'skipped');
  const parts: string[] = [];
  if (accepted.length > 0) parts.push(`${accepted.length} queued for review`);
  if (alreadyRunning.length > 0) parts.push(`${alreadyRunning.length} already in a running review job`);
  if (skipped.length > 0) parts.push(`${skipped.length} skipped`);
  return {
    accepted,
    alreadyRunning,
    skipped,
    headline: parts.length > 0 ? parts.join(' · ') : 'No documents were queued.',
  };
}

// ── Pagination + tabs (R3) ─────────────────────────────────────────

export type DocumentsTabKey =
  | 'all'
  | 'pending'
  | 'system_approved'
  | 'system_rejected'
  | 'further_human_review'
  | 'review_failed';

/**
 * Tabs are server bucket selectors: each maps to the `systemDecision`
 * query value the endpoint filters on (null = no bucket filter). The
 * transient 'Processing' bucket has no tab of its own — those rows
 * appear under All Documents while a scan is in flight.
 */
export const TAB_SYSTEM_DECISION: Record<DocumentsTabKey, string | null> = {
  all: null,
  pending: 'Pending Review',
  system_approved: 'System Approved',
  system_rejected: 'System Rejected',
  further_human_review: 'Further Human Review Required',
  review_failed: 'Review Failed',
};

export interface DocumentDecisionCounts {
  total: number;
  'Pending Review': number;
  Processing: number;
  'System Approved': number;
  'System Rejected': number;
  'Further Human Review Required': number;
  'Review Failed': number;
}

/** Tab badge counts come from the server `counts`, never the page. */
export function tabCountFromServerCounts(
  counts: DocumentDecisionCounts | null | undefined,
  tab: DocumentsTabKey
): number {
  if (!counts) return 0;
  switch (tab) {
    case 'all':
      return counts.total || 0;
    case 'pending':
      return counts['Pending Review'] || 0;
    case 'system_approved':
      return counts['System Approved'] || 0;
    case 'system_rejected':
      return counts['System Rejected'] || 0;
    case 'further_human_review':
      return counts['Further Human Review Required'] || 0;
    case 'review_failed':
      return counts['Review Failed'] || 0;
    default:
      return 0;
  }
}

export interface DocumentListQueryParams {
  search: string;
  program: string;
  docType: string;
  systemDecision: string | null;
  version: string;
  page: number;
  pageSize: number;
  groupByApplication: boolean;
}

/**
 * The list request ALWAYS carries page/pageSize (the endpoint's
 * no-params default returns the full set — the centre opts into
 * pagination explicitly) and groupBy=application in grouped mode.
 */
export function buildDocumentListQuery(params: DocumentListQueryParams): string {
  const queryParams = new URLSearchParams();
  if (params.search) queryParams.append('search', params.search);
  if (params.program) queryParams.append('program', params.program);
  if (params.docType) queryParams.append('docType', params.docType);
  if (params.systemDecision) queryParams.append('systemDecision', params.systemDecision);
  if (params.version) queryParams.append('version', params.version);
  queryParams.append('page', String(params.page));
  queryParams.append('pageSize', String(params.pageSize));
  if (params.groupByApplication) queryParams.append('groupBy', 'application');
  return queryParams.toString();
}

// ── Background review jobs (R2 / I2) ───────────────────────────────

export const ACTIVE_REVIEW_JOB_STATUSES: readonly string[] = [
  'queued',
  'validating',
  'processing',
];

export function isReviewJobActive(status: string | null | undefined): boolean {
  return !!status && ACTIVE_REVIEW_JOB_STATUSES.includes(status);
}

export function isReviewItemTerminal(status: string | null | undefined): boolean {
  return status === 'completed' || status === 'failed' || status === 'skipped' || status === 'cancelled';
}

export interface CentreReviewJobItem {
  id: string;
  status: string;
  entityId: string | null;
  docName: string | null;
  result: Record<string, any> | null;
  error: any;
}

export interface CentreReviewJob {
  id: string;
  status: string;
  totalItems: number;
  completedItems: number;
  failedItems: number;
  progressPercent: number;
  createdAt: string | null;
  completedAt: string | null;
  createdByName: string;
  items: CentreReviewJobItem[];
}

/** Normalize a by-id / submit response into the strip's job shape. */
export function normalizeCentreReviewJob(raw: any, previous?: CentreReviewJob): CentreReviewJob | null {
  const job = raw?.job || raw;
  if (!job || !(job.id || raw?.jobId)) return null;
  return {
    id: String(job.id || raw?.jobId),
    status: String(job.status || previous?.status || 'queued'),
    totalItems: typeof job.totalItems === 'number' ? job.totalItems : (previous?.totalItems ?? 0),
    completedItems: typeof job.completedItems === 'number' ? job.completedItems : (previous?.completedItems ?? 0),
    failedItems: typeof job.failedItems === 'number' ? job.failedItems : (previous?.failedItems ?? 0),
    progressPercent: typeof job.progressPercent === 'number' ? job.progressPercent : (previous?.progressPercent ?? 0),
    createdAt: job.createdAt || previous?.createdAt || null,
    completedAt: job.completedAt || previous?.completedAt || null,
    createdByName: job.createdByName || previous?.createdByName || '',
    items: Array.isArray(raw?.items)
      ? raw.items.map((i: any) => ({
          id: String(i.id || i.entityId || ''),
          status: String(i.status || ''),
          entityId: i.entityId || null,
          docName: i.docName || null,
          result: i.result || null,
          error: i.error ?? null,
        }))
      : (previous?.items ?? []),
  };
}

/**
 * Map a job's TERMINAL items to display outcomes via the shared
 * ApplicantDetail mapper (mapBulkReviewJobItem) — in-flight items are
 * excluded so a queued item never poses as a verdict.
 */
export function mapTerminalJobOutcomes(
  job: CentreReviewJob,
  mapItem: (item: any) => BulkReviewJobItemOutcome | null
): BulkReviewJobItemOutcome[] {
  return job.items
    .filter(i => isReviewItemTerminal(i.status))
    .map(i => mapItem(i))
    .filter(Boolean) as BulkReviewJobItemOutcome[];
}

export function countAutoApplied(outcomes: BulkReviewJobItemOutcome[]): number {
  return outcomes.filter(o => o.appliedState === 'auto_applied').length;
}

/** Raw item error text (job item errors arrive as object or string). */
export function reviewItemErrorText(item: { error?: any } | null | undefined): string | null {
  const err = item?.error;
  if (!err) return null;
  if (typeof err === 'string') return err;
  if (typeof err?.message === 'string') return err.message;
  return 'Review did not complete.';
}
